/**
 * 값 위치 검토 화면 조각 — 헤더 도구줄의 켜기 버튼, 지금 줄 옆에 붙는 검토 카드, DSD 머리 칸
 * 모서리 표시.
 *
 * 흐름: ① 값 존재(칸 색, 도구줄 버튼 옆 「못 찾음」 으로 알림) → ② 값 위치(이 도구).
 *
 * 검토 카드는 지금 줄의 머리 칸 바로 옆에 붙고 표를 어둡게 가리지 않는다. 카드 안의 칸별
 * 대응표(DSD 열·값 → IXD 위치)로 판단이 카드 안에서 끝나게 하고, 표는 확인하고 싶을 때만
 * 본다 — 띠 → 머리 → IXD → 띠 로 시선이 오가던 피로를 줄이려는 것이다. 지금 줄이 없을 때
 * (시작 전·끝·확인할 줄 없음)는 도구줄 버튼 아래에 상태 카드를 붙인다.
 *
 * 상태는 `usePositionCheck` 가 갖고 여기는 그리기만 한다.
 *
 * WHY: 문구는 "검토를 돕는 목록" 으로 읽혀야 한다. 끝까지 봤을 때 "모두 정상" 이라고 쓰면
 * 목록 밖(이름이 같은 줄, IXD 에 없는 값, 글자 값)까지 확인된 것처럼 읽힌다.
 */

import * as Popover from "@radix-ui/react-popover";
import { CircleHelp, ScanSearch } from "lucide-react";
import {
  createContext,
  type ReactNode,
  type SyntheticEvent,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/toggle-ui-kit/button";
import { IconButton } from "@/toggle-ui-kit/icon-button";
import { NumberBadge } from "@/toggle-ui-kit/badge";
import {
  type CounterpartAxis,
  isSameHeaderName,
} from "@/utils/ixd-dsd-compare/header-counterpart";
import { logPositionCheck } from "@/utils/usage-log";
import { CompareHelpPopover } from "../compare-help-popover";
import {
  type FlaggedLine,
  POSITION_REVIEW_TOGGLE_ATTR,
} from "./use-position-check";
import type { ValuePresence } from "./value-presence";

const DOT_CLASS: Record<"diff" | "split", string> = {
  diff: "bg-label-alternative",
  split: "bg-status-caution",
};

const Dot = ({ flag }: { flag: "diff" | "split" }) => (
  <span
    aria-hidden
    className={cn(
      "inline-block size-2 shrink-0 rounded-full ring-1 ring-static-white",
      DOT_CLASS[flag],
    )}
  />
);

const Kbd = ({ children }: { children: string }) => (
  <kbd className="rounded border border-current/40 px-1 font-mono caption-2-medium leading-4 opacity-80">
    {children}
  </kbd>
);

const SCOPE_NOTE =
  "검토를 돕는 목록이에요. 이름이 같은 줄, IXD에 없는 값(빨간 칸), 글자·문장 값은 여기 들어 있지 않으니 따로 확인해 주세요.";

const CARD_CLASS =
  "z-40 flex flex-col gap-2.5 rounded-[10px] border border-line-normal bg-background-base p-3 caption-1-medium text-label-neutral shadow-large";

/** 카드 안의 「이전」·「다음」 — 도구줄의 다른 버튼과 같은 28px */
const NavButton = ({
  children,
  onClick,
  className,
}: {
  children: ReactNode;
  onClick: () => void;
  className?: string;
}) => (
  <Button
    variant="outlined"
    color="secondary"
    size={28}
    onClick={onClick}
    className={cn("shrink-0 bg-background-base", className)}
  >
    {children}
  </Button>
);

const PrimaryButton = ({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick: () => void;
}) => (
  <Button
    variant="solid"
    color="blue"
    size={28}
    onClick={onClick}
    className="shrink-0"
  >
    {children}
  </Button>
);

const HelpButton = () => (
  <CompareHelpPopover defaultTab="position-check">
    <IconButton
      variant="transparent"
      icon={<CircleHelp />}
      aria-label="값 위치 검토 도움말"
      className="text-label-alternative"
      onClick={() => logPositionCheck("도움말")}
    />
  </CompareHelpPopover>
);

// 포털로 그려도 React 이벤트는 머리 칸(th)까지 올라가 칸 클릭으로 처리된다. 카드 안 조작은
// 칸 클릭이 아니므로 여기서 멈춘다.
const stopToCell = (event: { stopPropagation: () => void }) =>
  event.stopPropagation();

// ============================================
// 헤더 도구줄 — 켜기 버튼 (+ 지금 줄이 없을 때 상태 카드)
// ============================================

interface PositionReviewToggleProps {
  /** 라벨 사전이 없으면(편집 모드 등) 쓸 수 없다 */
  available: boolean;
  active: boolean;
  /** 확인해 볼 만한 줄 수 */
  flaggedCount: number;
  presence: ValuePresence;
  showNewBadge: boolean;
  onToggle: () => void;
  /** 켜져 있는데 지금 줄이 없을 때(시작 전·끝·확인할 줄 없음) 버튼 아래에 붙일 카드 */
  statusCard?: ReactNode;
}

/**
 * 도구줄의 「값 위치 검토」 버튼. 꺼져 있을 때는 아직 판정하지 않은 줄 수(경고색)와 NEW 로 도구가 있음을
 * 알리고, 켜져 있을 때는 채운 버튼 「위치 검토 중」 으로 모드를 보여 준다. 옆의 「못 찾음 n」
 * 은 1단계(값 존재)를 먼저 보라는 신호다.
 */
export const PositionReviewToggle = ({
  available,
  active,
  flaggedCount,
  presence,
  showNewBadge,
  onToggle,
  statusCard,
}: PositionReviewToggleProps) => {
  const comparable = presence.found + presence.nearlyFound + presence.notFound;
  const presenceTitle = `① 값 존재 — IXD에서 찾은 표 값 ${presence.found + presence.nearlyFound}/${comparable} (일치 ${presence.found} · 부호·단수차 ${presence.nearlyFound} · 못 찾음 ${presence.notFound})`;
  return (
    <div className="flex items-center gap-1.5">
      {presence.notFound > 0 && (
        <span
          title={`${presenceTitle}. 빨간 칸부터 확인하세요.`}
          className="flex items-center gap-1 rounded-full bg-status-negative px-2 caption-1-semibold leading-6 text-static-white"
        >
          못 찾음 {presence.notFound}
        </span>
      )}
      <Popover.Root open={!!statusCard}>
        <Popover.Anchor asChild>
          {/* 검토 중 Enter 를 막을 대상 — 훅이 이 표시로 켜기 버튼 하나만 가린다 */}
          <Button
            {...{ [POSITION_REVIEW_TOGGLE_ATTR]: "" }}
            variant={active ? "solid" : "outlined"}
            color={active ? "blue" : "secondary"}
            size={28}
            aria-pressed={active}
            disabled={!available}
            title={
              available
                ? `② 값 위치 — 값이 맞는 행·열에 들어갔는지 확인해 볼 만한 줄을 차례로 보여 줘요. ${presenceTitle}`
                : "편집 중에는 쓸 수 없어요"
            }
            onClick={onToggle}
            leftIcon={<ScanSearch />}
            className={cn(
              "gap-1.5 disabled:cursor-not-allowed",
              !active && "bg-background-base",
            )}
          >
            <span className="flex items-center gap-1.5">
              {active ? "위치 검토 중" : "값 위치 검토"}
              {available && <Kbd>{active ? "Esc" : "I"}</Kbd>}
              {!active && available && flaggedCount > 0 && (
                <NumberBadge className="bg-status-caution text-static-white">
                  {flaggedCount}
                </NumberBadge>
              )}
              {!active && available && showNewBadge && (
                <span className="rounded-full bg-primary-color px-1.5 caption-2-semibold leading-4 text-static-white">
                  NEW
                </span>
              )}
            </span>
          </Button>
        </Popover.Anchor>
        <Popover.Portal>
          <Popover.Content
            side="bottom"
            align="end"
            sideOffset={8}
            collisionPadding={12}
            onOpenAutoFocus={(event) => event.preventDefault()}
            onInteractOutside={(event) => event.preventDefault()}
            className={cn(CARD_CLASS, "w-[360px]")}
          >
            {statusCard}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
};

interface PositionReviewStatusProps {
  total: number;
  finished: boolean;
  onNext: () => void;
  /** 끝까지 본 뒤에는 마지막 줄로 돌아가 다시 볼 수 있다 */
  onPrev: () => void;
}

/** 지금 줄이 없을 때의 상태 카드 내용 — 시작 전·끝·확인할 줄 없음 */
export const PositionReviewStatus = ({
  total,
  finished,
  onNext,
  onPrev,
}: PositionReviewStatusProps) => (
  <>
    {total === 0 ? (
      <span className="label-2-medium text-label-normal">
        이 화면의 표에는 이름이 머리와 다른 줄이 없어요.
      </span>
    ) : finished ? (
      <span className="label-2-medium text-label-normal">
        골라 둔 {total}줄을 끝까지 봤어요.
      </span>
    ) : (
      <span className="label-2-medium text-label-normal">
        이 화면에서 확인해 볼 만한 줄 {total}개가 있어요.
      </span>
    )}
    <span className="text-label-alternative">{SCOPE_NOTE}</span>
    {total > 0 && (
      <div className="flex items-center justify-end gap-1.5">
        {finished && (
          <NavButton className="mr-auto" onClick={onPrev}>
            이전 <Kbd>←</Kbd>
          </NavButton>
        )}
        <HelpButton />
        {!finished && (
          <PrimaryButton onClick={onNext}>
            첫 줄로 <Kbd>→</Kbd>
          </PrimaryButton>
        )}
      </div>
    )}
  </>
);

// ============================================
// 지금 줄 옆 검토 카드
// ============================================

interface PositionReviewCardProps {
  line: FlaggedLine;
  /**
   * 표가 DL 영역 여럿에 걸칠 때 이 줄의 영역 제목 — 같은 머리에 영역별 줄이 따로 있어 어느
   * 영역을 보는지 알려야 한다. 영역이 하나뿐인 표면 null
   */
  regionTitle?: string | null;
  cursor: number;
  total: number;
  headerTextOf: (dsdId: string) => string;
  onPrev: () => void;
  onNext: () => void;
}

/**
 * 지금 줄의 검토 카드. 이름만 다른 줄은 「DSD 머리 → IXD 태그」 하나로 충분하다 — 칸이 모두
 * 같은 태그로 갔기 때문이다. IXD 여러 곳으로 갈라진 줄은 칸마다 "DSD 다른 축 머리 · 값 →
 * IXD 위치" 를 한 줄씩 보여 줘, 어느 값이 어디로 갔는지 가로로 읽어 판단하게 한다. 이 줄을
 * 가르는 IXD 이름이 머리와 다르면 빨간 글씨다.
 */
export const PositionReviewCard = ({
  line,
  regionTitle,
  cursor,
  total,
  headerTextOf,
  onPrev,
  onNext,
}: PositionReviewCardProps) => {
  const header = headerTextOf(line.counterpart.anchorDsdId).trim();
  const crossLabel = line.counterpart.axis === "row" ? "DSD 열" : "DSD 행";
  return (
    <>
      <div className="flex items-center gap-2">
        <span className="tabular-nums text-label-alternative">
          {cursor + 1}/{total}
        </span>
        <span className="flex items-center gap-1 rounded bg-background-neutral-alternative px-1.5 caption-2-semibold leading-4 text-label-neutral">
          <Dot flag={line.flag} />
          {line.flag === "split" ? "IXD 여러 곳" : "이름 다름"}
        </span>
        {line.flag === "split" && (
          <span className="truncate label-1-normal-semibold text-label-normal">
            {header}
          </span>
        )}
        <span className="ml-auto">
          <HelpButton />
        </span>
      </div>
      {regionTitle && (
        <span
          title={regionTitle}
          className="-mt-1 truncate caption-2-medium text-label-alternative"
        >
          DL 영역 · {regionTitle}
        </span>
      )}
      {line.flag === "diff" ? (
        // 두 칸의 폭과 네 줄 높이를 미리 잡아 둔다 — 이름 길이에 따라 상자가 늘었다 줄었다 하지
        // 않게. 네 줄을 넘는 이름은 말줄임하고 마우스를 올리면 전체를 보여 준다.
        <div className="flex flex-col gap-1">
          <div className="grid grid-cols-[minmax(0,1fr)_16px_minmax(0,1fr)] items-center gap-1.5">
            <span className="flex min-w-0 flex-col rounded-small bg-background-neutral-normal px-3 py-1.5">
              <span className="caption-2-semibold text-label-alternative">
                DSD 머리
              </span>
              <span
                title={header}
                className="line-clamp-4 h-16 break-all caption-1-semibold text-label-normal"
              >
                {header}
              </span>
            </span>
            <span aria-hidden className="text-center text-label-alternative">
              →
            </span>
            {/* 「IXD 태그」 쪽은 빨간 글씨가 신호다 — 면색까지 옅은 빨강으로 하려면 토큰
                등록이 필요해 여기서는 같은 중립 면색을 쓴다 */}
            <span className="flex min-w-0 flex-col rounded-small bg-background-neutral-normal px-3 py-1.5">
              <span className="caption-2-semibold text-label-alternative">
                IXD 태그
              </span>
              <span
                title={line.counterpart.destinations[0]?.label}
                className="line-clamp-4 h-16 break-all caption-1-semibold text-status-negative"
              >
                {line.counterpart.destinations[0]?.label}
              </span>
            </span>
          </div>
          {line.counterpart.unmatchedDsdIds.length > 0 && (
            <span className="text-label-alternative">
              IXD에 없는 칸 {line.counterpart.unmatchedDsdIds.length}개 제외
            </span>
          )}
        </div>
      ) : (
        <div className="max-h-[220px] overflow-y-auto">
          <table className="w-full table-fixed border-collapse">
            <colgroup>
              <col className="w-[26%]" />
              <col className="w-[22%]" />
              <col className="w-5" />
              <col />
            </colgroup>
            <thead>
              <tr className="caption-2-medium text-label-alternative">
                <th className="py-1 pr-2 text-left font-semibold">
                  {crossLabel}
                </th>
                <th className="py-1 pr-2 text-right font-semibold">값</th>
                <th aria-hidden />
                <th className="py-1 text-left font-semibold">IXD 위치</th>
              </tr>
            </thead>
            <tbody>
              {line.counterpart.cells.map((cell) => {
                const cross = cell.crossAnchorDsdId
                  ? headerTextOf(cell.crossAnchorDsdId).trim()
                  : "—";
                const value = headerTextOf(cell.dsdId).trim();
                const ixd =
                  cell.axisLabel === null
                    ? "IXD에 없음"
                    : [cell.axisLabel, cell.restLabel]
                        .filter(Boolean)
                        .join(" · ");
                return (
                  <tr key={cell.dsdId} className="border-t border-line-normal">
                    <td
                      title={cross}
                      className="truncate py-1 pr-2 text-label-normal"
                    >
                      {cross}
                    </td>
                    <td
                      title={value}
                      className="truncate py-1 pr-2 text-right font-mono text-label-normal"
                    >
                      {value}
                    </td>
                    <td aria-hidden className="px-1 text-label-alternative">
                      →
                    </td>
                    <td title={ixd} className="truncate py-1">
                      {cell.axisLabel === null ? (
                        <span className="text-label-alternative">
                          IXD에 없음
                        </span>
                      ) : (
                        <>
                          <span
                            className={cn(
                              "caption-1-semibold",
                              isSameHeaderName(header, cell.axisLabel)
                                ? "text-label-normal"
                                : "text-status-negative",
                            )}
                          >
                            {cell.axisLabel}
                          </span>
                          {cell.restLabel && (
                            <span className="text-label-alternative">
                              {" "}
                              · {cell.restLabel}
                            </span>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex items-center justify-between gap-1.5">
        <NavButton onClick={onPrev}>
          이전 <Kbd>←</Kbd>
        </NavButton>
        <PrimaryButton onClick={onNext}>
          다음 <Kbd>→</Kbd>
        </PrimaryButton>
      </div>
    </>
  );
};

// ============================================
// 머리 칸 모서리 표시 (+ 지금 줄이면 검토 카드)
// ============================================

interface PositionCheckMarkerProps {
  flag: "diff" | "split";
  /** 이 표시가 가리키는 줄의 키 — 지금 줄인지는 context 로 본다 */
  lineKey: string;
  onJump: () => void;
  /** 열 머리면 카드를 오른쪽에 띄운다 — 아래로 띄우면 그 열의 값을 가린다 */
  axis: CounterpartAxis;
}

/**
 * 지금 보는 줄과 그 카드. prop 으로 내리면 줄을 옮길 때마다 표시 맵이 새로 만들어져 화면의
 * **모든** 표 블록이 리렌더된다(`memo` 가 맵 참조로 깨진다). context 는 `memo` 를 통과하므로
 * 이동 한 번에 다시 그려지는 것이 "떠난 표시 + 새 표시" 둘로 줄어든다.
 */
const PositionCurrentLineContext = createContext<{
  currentKey: string | null;
  card: ReactNode;
}>({ currentKey: null, card: null });

export const PositionCurrentLineProvider = PositionCurrentLineContext.Provider;

/** 검토 카드가 벗어나면 안 되는 영역 — DSD 미리보기 스크롤 영역에 이 속성을 단다 */
export const POSITION_CARD_BOUNDARY_ATTR = "data-position-card-boundary";

/** 검토 중에도 눌리는 칸·버튼 — 표시 점과 줄의 머리 칸에 단다 */
export const POSITION_REVIEW_CONTROL_ATTR = "data-position-review-control";

/**
 * 검토 중 화면 영역의 마우스 입력을 기존 동작(셀 선택·드래그·우클릭 메뉴·셀 클릭 이동)에 넘기지
 * 않는다. 영역에 캡처 핸들러로 단다 — React 루트에서 전파를 끊으므로 안쪽에 직접 붙인 리스너도
 * 받지 못한다. 스크롤은 막지 않는다.
 */
export const blockWhileReviewing = (event: SyntheticEvent) => {
  const target = event.target;
  // 검토 카드는 포털이라 DOM 으로는 영역 밖이지만 React 트리로는 이 안에서 올라온다.
  if (!(target instanceof Node) || !event.currentTarget.contains(target))
    return;
  if (
    target instanceof Element &&
    target.closest(`[${POSITION_REVIEW_CONTROL_ATTR}]`)
  )
    return;
  event.preventDefault();
  event.stopPropagation();
};

/**
 * 머리 칸 오른쪽 위 점.
 * 누르면 그 줄로 검토 위치를 옮긴다 — 순회의 보조 경로다. 지금 줄이면 검토 카드를 바로 아래에
 * 붙인다(화면 끝이면 위로 뒤집는다). 카드는 표를 가리는 배경을 깔지 않는다.
 *
 * WHY: 카드는 DSD 미리보기 안에서만 움직인다. 화면 전체를 경계로 두면 왼쪽으로 밀려 나가
 * 왼쪽 사이드바(z-50) 밑에 깔린다. 미리보기가 좁으면 카드 폭을 그만큼 줄인다.
 */
export const PositionCheckMarker = ({
  flag,
  lineKey,
  onJump,
  axis,
}: PositionCheckMarkerProps) => {
  const current = useContext(PositionCurrentLineContext);
  const active = current.currentKey === lineKey;
  const card = active ? current.card : null;
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [boundary, setBoundary] = useState<Element | null>(null);
  useLayoutEffect(() => {
    if (!card) return;
    setBoundary(
      anchorRef.current?.closest(`[${POSITION_CARD_BOUNDARY_ATTR}]`) ?? null,
    );
  }, [card]);
  return (
    <Popover.Root open={!!card}>
      <Popover.Anchor asChild>
        <button
          ref={anchorRef}
          type="button"
          {...{ [POSITION_REVIEW_CONTROL_ATTR]: "" }}
          aria-label={
            flag === "split"
              ? "IXD 여러 곳으로 갈라진 줄"
              : "IXD 이름이 머리와 다른 줄"
          }
          className={cn(
            "flex h-4 min-w-4 items-center justify-center rounded-full px-0.5 hover:bg-black/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500",
            active && "ring-2 ring-blue-500",
          )}
          onClick={(event) => {
            event.stopPropagation();
            onJump();
          }}
        >
          <Dot flag={flag} />
        </button>
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          aria-label="값 위치 검토 카드"
          side={axis === "col" ? "right" : "bottom"}
          align={axis === "col" ? "start" : "end"}
          sideOffset={6}
          collisionPadding={8}
          collisionBoundary={boundary}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onClick={stopToCell}
          onPointerDown={stopToCell}
          className={cn(
            CARD_CLASS,
            "w-[400px] max-w-[var(--radix-popover-content-available-width)]",
          )}
        >
          {card}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
